
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SearchInput from '../../components/base/SearchInput';
import Button from '../../components/base/Button';
import { featuredPlaylists, trendingTracks, recentlyPlayed } from '../../mocks/musicData';
import { useMusicPlayer } from '../../hooks/useMusicPlayer';

export default function LibraryPage() {
  const [activeTab, setActiveTab] = useState('playlists');
  const [searchQuery, setSearchQuery] = useState('');
  const [likedTracks, setLikedTracks] = useState<number[]>([1, 3, 5, 7, 9]);
  const { playTrack } = useMusicPlayer();
  const navigate = useNavigate();

  const tabs = [
    { id: 'playlists', label: 'Playlists', icon: 'ri-play-list-line' },
    { id: 'liked', label: 'Liked Songs', icon: 'ri-heart-line' },
    { id: 'recent', label: 'Recently Played', icon: 'ri-time-line' }
  ];

  const userPlaylists = [
    ...featuredPlaylists.slice(0, 3),
    {
      id: 101,
      title: "My Favorites",
      description: "Personal collection of favorite tracks",
      imageUrl: "https://readdy.ai/api/search-image?query=personal%20music%20collection%20playlist%20cover%20with%20heart%20symbol%2C%20warm%20colors%2C%20cozy%20atmosphere%2C%20music%20notes%2C%20personal%20touch&width=300&height=300&seq=my-favorites&orientation=squarish",
      trackCount: 47,
      plays: "1.2K",
      isPrivate: true,
      genre: "Mixed"
    },
    {
      id: 102,
      title: "Workout Mix",
      description: "High energy tracks for gym sessions",
      imageUrl: "https://readdy.ai/api/search-image?query=energetic%20workout%20playlist%20cover%20with%20fitness%20theme%2C%20dynamic%20colors%2C%20exercise%20equipment%2C%20motivational%20design&width=300&height=300&seq=workout-mix&orientation=squarish",
      trackCount: 32,
      plays: "856",
      isPrivate: false,
      genre: "Electronic"
    }
  ];

  const likedSongs = trendingTracks.filter(track => likedTracks.includes(track.id));

  const filteredContent = () => {
    switch (activeTab) {
      case 'playlists':
        return userPlaylists.filter(playlist => 
          playlist.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          playlist.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
      case 'liked':
        return likedSongs.filter(track =>
          track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          track.artist.toLowerCase().includes(searchQuery.toLowerCase())
        );
      case 'recent':
        return recentlyPlayed.filter(track =>
          track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          track.artist.toLowerCase().includes(searchQuery.toLowerCase())
        );
      default:
        return [];
    }
  };

  const toggleLike = (trackId: number) => {
    setLikedTracks(prev => 
      prev.includes(trackId) 
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  const handlePlayTrack = (track: any) => {
    playTrack(track);
  };

  const handlePlayPlaylist = (playlist: any) => {
    if (playlist.tracks && playlist.tracks.length > 0) {
      playTrack(playlist.tracks[0]);
    }
  };

  const handleTrackSelect = (track: any) => {
    playTrack(track);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onTrackSelect={handleTrackSelect} />
      
      <main className="pt-8 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-600 to-pink-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-6">
              <div className="w-32 h-32 bg-white/20 rounded-2xl flex items-center justify-center">
                <i className="ri-music-2-fill text-5xl text-white"></i>
              </div>
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-white mb-2">Your Library</h1>
                <p className="text-xl text-purple-100 mb-4">Your personal music collection and playlists</p>
                <div className="flex items-center space-x-6 text-purple-100">
                  <span>{userPlaylists.length} playlists</span>
                  <span>{likedSongs.length} liked songs</span>
                  <span>{recentlyPlayed.length} recently played</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="bg-white border-b border-gray-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <div className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${tab.icon} text-lg`}></i>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-64">
                  <SearchInput 
                    onSearch={setSearchQuery}
                    placeholder={`Search ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()}...`}
                  />
                </div>
                <Button onClick={() => navigate('/upload')}>
                  <i className="ri-add-line mr-2"></i>
                  Create Playlist
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {activeTab === 'playlists' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredContent().map((playlist: any) => (
                  <div 
                    key={playlist.id}
                    className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  >
                    <div className="relative overflow-hidden rounded-t-xl">
                      <img 
                        src={playlist.imageUrl}
                        alt={playlist.title}
                        className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPlaylist(playlist);
                          }}
                          className="w-12 h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-full opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 flex items-center justify-center cursor-pointer"
                        >
                          <i className="ri-play-fill text-lg"></i>
                        </button>
                      </div>
                      {playlist.isPrivate && (
                        <div className="absolute top-3 right-3">
                          <div className="bg-black/50 backdrop-blur-sm rounded-full p-2">
                            <i className="ri-lock-line text-white text-sm"></i>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 truncate">{playlist.title}</h3>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{playlist.description}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{playlist.trackCount} tracks</span>
                        <span>{playlist.plays} plays</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(activeTab === 'liked' || activeTab === 'recent') && (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <div className="space-y-2">
                    {filteredContent().map((track: any, index: number) => (
                      <div 
                        key={track.id}
                        className="flex items-center space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
                      >
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-400 w-8 text-center">
                            {index + 1}
                          </div>
                          <div className="relative">
                            <img 
                              src={track.imageUrl}
                              alt={track.title}
                              className="w-12 h-12 rounded-lg object-cover object-top"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-all duration-200 flex items-center justify-center">
                              <button 
                                onClick={() => handlePlayTrack(track)}
                                className="w-6 h-6 bg-white text-purple-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center"
                              >
                                <i className="ri-play-fill text-xs"></i>
                              </button>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">{track.title}</h4>
                            <p className="text-sm text-gray-600 truncate">{track.artist}</p>
                          </div>
                        </div>

                        <div className="hidden md:block text-sm text-gray-600 min-w-0 w-32">
                          {track.album}
                        </div>

                        {activeTab === 'recent' && track.playedAt && (
                          <div className="hidden lg:block text-sm text-gray-500 min-w-0 w-24">
                            {track.playedAt}
                          </div>
                        )}

                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => toggleLike(track.id)}
                            className={`transition-colors cursor-pointer ${
                              likedTracks.includes(track.id) 
                                ? 'text-red-500' 
                                : 'text-gray-400 hover:text-red-500'
                            }`}
                          >
                            <i className={`ri-heart-${likedTracks.includes(track.id) ? 'fill' : 'line'} text-lg`}></i>
                          </button>
                          <div className="text-sm text-gray-500 min-w-0 w-12 text-right">
                            {track.duration}
                          </div>
                          <button className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                            <i className="ri-more-2-fill text-lg"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {filteredContent().length === 0 && (
              <div className="text-center py-16">
                <i className={`${tabs.find(t => t.id === activeTab)?.icon} text-6xl text-gray-300 mb-4 block`}></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No {tabs.find(t => t.id === activeTab)?.label.toLowerCase()} found
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery ? 'Try adjusting your search' : `Start building your ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()}`}
                </p>
                {activeTab === 'playlists' && (
                  <Button onClick={() => navigate('/upload')}>
                    <i className="ri-add-line mr-2"></i>
                    Create Your First Playlist
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
